import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useRef, useState, useCallback, useEffect, useImperativeHandle } from 'react';
const ApprovalPanel = React.forwardRef(function ApprovalPanel(props, ref) {
    const { fileId, workflowName, canApprove, hasAlreadyReviewed, approveTitle, rejectTitle, userName, userId, history: initialHistory, remainingReviewers: initialReviewers, beforeApprovalChange, onApprovalChange, } = props;
    const containerRef = useRef(null);
    const [history, setHistory] = useState(initialHistory);
    const [reviewers, setReviewers] = useState(initialReviewers);
    const [reviewed, setReviewed] = useState(hasAlreadyReviewed);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    // Reset state when props change (file switch)
    useEffect(() => {
        setHistory(initialHistory);
        setReviewers(initialReviewers);
        setReviewed(hasAlreadyReviewed);
        setComment('');
        setSubmitting(false);
        setCollapsed(false);
    }, [fileId, initialHistory, initialReviewers, hasAlreadyReviewed]);
    useImperativeHandle(ref, () => ({
        getContainerElement: () => containerRef.current,
    }), []);
    const handleSubmit = useCallback((approved) => {
        if (submitting)
            return;
        // Allow callers to cancel the submission
        if (beforeApprovalChange && beforeApprovalChange(fileId, approved) === false) {
            return;
        }
        setSubmitting(true);
        const endpoint = approved ? 'ajax/approval.php?approve' : 'ajax/approval.php?reject';
        $.post(endpoint, { file_id: fileId, comment }, function (data) {
            let response;
            try {
                response = JSON.parse(data);
            }
            catch {
                alert('Invalid response from server.');
                setSubmitting(false);
                return;
            }
            if (!response.success) {
                alert(response.error || 'Unknown error');
                setSubmitting(false);
                return;
            }
            const newWorkflowId = response.new_workflow_id;
            // Update workflow dropdown if workflow changed
            if (newWorkflowId != null) {
                const dropdown = document.getElementById('player-file-workflow-dropdown');
                if (dropdown)
                    dropdown.value = String(newWorkflowId);
            }
            // Optimistic UI: hide form, add to history, remove from reviewers
            setReviewed(true);
            const newEntry = {
                userId,
                userName,
                avatarUrl: `avatar?name=${encodeURIComponent(userName)}`,
                approved,
                comment,
                date: Date.now() / 1000,
            };
            setHistory(prev => [newEntry, ...prev]);
            setReviewers(prev => prev.filter(r => r.userId !== userId));
            setComment('');
            setSubmitting(false);
            // Notify caller of successful change
            if (onApprovalChange) {
                onApprovalChange(fileId, approved, newWorkflowId);
            }
        }).fail(function (_jqXHR, _textStatus, errorString) {
            alert('Failed to submit review: ' + errorString);
            setSubmitting(false);
        });
    }, [fileId, comment, submitting, userId, userName, beforeApprovalChange, onApprovalChange]);
    const formatRelativeDate = (timestamp, noFutureDates = true) => {
        if (!timestamp)
            return '';
        let ms = timestamp * 1000;
        if (noFutureDates) {
            // Make sure no dates are in the future
            ms = Math.min(timestamp * 1000, Date.now() - 2000);
        }
        if (typeof moment !== 'undefined') {
            return moment(new Date(ms)).fromNow();
        }
        return new Date(ms).toLocaleString();
    };
    const handleApprove = useCallback(() => handleSubmit(true), [handleSubmit]);
    const handleReject = useCallback(() => handleSubmit(false), [handleSubmit]);
    const toggleCollapsed = useCallback(() => setCollapsed(prev => !prev), []);
    const showForm = workflowName && canApprove && !reviewed;
    const showPanel = showForm || history.length > 0 || reviewers.length > 0;
    if (!showPanel)
        return null;
    const reviewerList = reviewers.sort((a, b) => Number(b.isCurrentUser) - Number(a.isCurrentUser));
    return (_jsxs("div", { id: "approval-list", ref: containerRef, className: collapsed ? 'collapsed' : '', role: "presentation", children: [showForm && (_jsxs("div", { id: "approval-container", className: "pageseccolor", role: "presentation", children: [_jsx("button", { onClick: toggleCollapsed, style: { border: 'none',
                            padding: 0,
                            margin: 0,
                            maxWidth: 'initial',
                            backgroundColor: 'transparent' }, "aria-label": "Toggle collapsed", children: _jsxs("h3", { children: [collapsed ? '▶' : '▼', " Approval"] }) }), _jsxs("div", { id: "approval-button-area", role: "presentation", children: [_jsx("textarea", { id: "approval_comment", placeholder: "Approval comment (optional)", value: comment, onChange: e => setComment(e.target.value), disabled: submitting, "aria-label": "Approval comment (optional)" }), _jsx("button", { className: "actionbtn approve", onClick: handleApprove, disabled: submitting, "aria-label": "Approve", children: approveTitle }), _jsx("button", { className: "actionbtn reject", onClick: handleReject, disabled: submitting, "aria-label": "Reject", children: rejectTitle })] })] })), _jsxs("div", { id: "approval-history-container", role: "list", children: [history.map((item, i) => (_jsxs("div", { className: `approval-history ${item.approved ? 'approved' : 'rejected'}`, "data-user_id": item.userId, role: "listitem", children: [_jsx("img", { src: item.avatarUrl, alt: "", className: "avatar" }), _jsx("span", { className: "avatar" }), item.userName, " ", item.approved ? 'approved' : 'rejected', item.comment && (_jsx("div", { className: "approval-comment", children: item.comment })), _jsx("div", { className: "approval-date", children: formatRelativeDate(item.date) })] }, `${item.userId}-${i}`))), reviewerList.map(reviewer => (_jsxs("div", { className: "approval-history", "data-user_id": reviewer.userId, children: [_jsx("img", { src: reviewer.avatarUrl, className: "avatar", "aria-hidden": "true" }), _jsx("span", { className: "avatar", children: reviewer.isCurrentUser
                                    ? 'You need to review'
                                    : `${reviewer.userName} needs to review` })] }, reviewer.userId)))] })] }));
});
export default ApprovalPanel;
